import React from 'react';
import SectionHeading from './SectionHeading';
import useScrollReveal from '../hooks/useScrollReveal';

const ROLES = [
  {
    period: 'Oct 2025 to now',
    org: 'Aerveil AI, Inc.',
    title: 'Founder and CEO',
    place: 'San Jose, CA',
    body:
      'Founded Aerveil to put an operational frost and spray decision in the hands of every grower who farms by block. I own the science: which physical models we trust, what thresholds fire an alert, and where the engine stays silent because the forecast will not support a call.',
    points: [
      'Taking on first Napa growers this season as design partners',
      'Own the model selection, thresholds, and the last check before any call goes out',
      'Delivery over WhatsApp so there is no app for a grower to learn',
    ],
  },
  {
    period: 'Late 2024',
    org: 'A deliberate pivot',
    title: 'From operational forecasting to agentic AI',
    place: '',
    body:
      'Nine years of operational forecasting had already made the case: routing between many data sources, grounding an answer in authoritative data, and making a hard call under time pressure. Agentic architectures solve the same problems. The move was an evolution of the same work, not a departure from it.',
    points: [],
    marker: true,
  },
  {
    period: '2016 to Oct 2025',
    org: 'National Center for Atmospheric Research (NCAR)',
    title: 'Software Engineer',
    place: 'Boulder, Colorado',
    body:
      'Nine years building the operational weather and hydrology systems that NOAA, NASA, the USGS and the NSF run on. Systems that ran on a fixed schedule, were measured against what actually happened, and had people making operational decisions on their output. Student assistant on the DICast forecasting engine from 2016, then software engineer from 2017.',
    points: [
      'Two programs in the Research Applications Laboratory: Weather Systems and Assessment through 2020, on surface transportation, renewable energy and forecast verification, then Hydrometeorological Applications from 2020, on operational hydrology',
      'Technical lead on the forcing pipeline feeding NOAA’s National Water Model, which forecasts streamflow for over 3 million stream channels',
      'Engineered a 65 TB, 1 km climate data pipeline spanning 1979 to 2022, now used nationwide',
      'Built and ran a real time machine learning system predicting runway safety at Minneapolis Saint Paul and Denver international airports',
      'Technical and operational lead for an AWS training platform serving 150+ simultaneous scientists',
      'Named the group’s in house AWS expert after designing its first cloud native microservices deployment',
    ],
  },
  {
    period: 'Aug 2014 to Jan 2015',
    org: 'Indian Institute of Technology Kanpur',
    title: 'Project Associate',
    place: 'Kanpur, India',
    body:
      'Built an automatic summarisation tool for Sanskrit in Java, covering sentence segmentation, lexical analysis, stop word elimination and stemming, with TF-IDF ranking to extract sentence summaries.',
    points: [],
  },
];

const GRAD_PHOTOS = [
  {
    src: '/img/cu-boulder-commencement.jpg',
    alt: 'Ishita Srivastava in cap and gown at the University of Colorado Boulder sign',
    caption: 'University of Colorado Boulder',
    meta: 'MS Computer Science, 2017',
  },
  {
    src: '/img/cs-department.jpg',
    alt: 'Ishita Srivastava in cap and gown beside the Department of Computer Science plaque',
    caption: 'Department of Computer Science',
    meta: 'Data science, ML and NLP',
  },
];

const CREDLY_URL =
  'https://www.credly.com/badges/4d9fd5f0-b140-4ac0-8f11-5bf75228f45e/public_url';

const DEGREES = [
  {
    degree: 'Master of Science, Computer Science',
    school: 'University of Colorado Boulder',
    period: '2015 to 2017',
    gpa: '3.85 / 4.0',
    detail:
      'Data science, machine learning, natural language processing and computer graphics, plus three semesters as a teaching assistant for Data Structures.',
    learnings: [
      'What stuck was that the data sits at the centre of every problem, not the model. Understanding what a dataset actually measures, where it is thin, and what has to be corrected before anyone leans on its output is most of the work. Every system I have built since has confirmed it.',
      'Most of the coursework was natural language processing, which is why moving into agentic systems years later did not feel like starting over.',
      'Teaching Data Structures for three semesters taught me that if I cannot explain a system plainly, I do not understand it yet. That has been more useful than anything else I took away.',
    ],
  },
  {
    degree: 'Bachelor of Technology, Information Technology',
    school: 'Uttar Pradesh Technical University, India',
    period: '2010 to 2014',
    gpa: '3.75 / 4.0',
    detail:
      'The engineering fundamentals: databases, networks, and building an application end to end before specialising in anything.',
    learnings: [],
  },
];

const Role = ({ role, isLast }) => {
  const [ref, isVisible] = useScrollReveal({ threshold: 0.1 });

  return (
    <li
      ref={ref}
      className={`relative pl-8 transition-all duration-700 ease-calm md:pl-12 ${
        isLast ? '' : 'pb-14'
      } ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
    >
      {!isLast && (
        <span aria-hidden="true" className="absolute left-0 top-3 bottom-0 w-px bg-ink/15" />
      )}
      <span
        aria-hidden="true"
        className={`absolute left-0 top-2.5 h-2 w-2 -translate-x-1/2 rounded-full ${
          role.marker ? 'bg-amber-bright' : 'bg-ink/30'
        }`}
      />

      <p className="metric text-[0.7rem] tracking-eyebrow text-amber-deep">{role.period}</p>
      <h3 className="display-md mt-3 text-ink">{role.title}</h3>
      <p className="mt-2 font-mono text-[0.72rem] uppercase tracking-eyebrow text-ink-soft">
        {role.org}
        {role.place && <span className="text-ink-soft/70"> · {role.place}</span>}
      </p>
      <p className="prose-body mt-5">{role.body}</p>

      {role.points.length > 0 && (
        <ul className="mt-5 space-y-2.5">
          {role.points.map((p) => (
            <li key={p} className="flex gap-3 text-[0.925rem] leading-relaxed text-ink-muted">
              <span aria-hidden="true" className="mt-2 h-px w-3 flex-none bg-amber-deep/60" />
              <span className="max-w-measure">{p}</span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

const Degree = ({ item }) => {
  const [ref, isVisible] = useScrollReveal({ threshold: 0.1 });

  return (
    <div
      ref={ref}
      className={`border-t border-ink/15 pt-8 transition-all duration-700 ease-calm ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
    >
      <p className="metric text-[0.7rem] tracking-eyebrow text-amber-deep">{item.period}</p>
      <h4 className="display-md mt-3 text-ink">{item.degree}</h4>
      <p className="mt-2 font-mono text-[0.72rem] uppercase tracking-eyebrow text-ink-soft">
        {item.school}
      </p>
      <p className="metric mt-2 text-sm text-ink-muted">GPA {item.gpa}</p>
      <p className="prose-body mt-5">{item.detail}</p>

      {item.learnings.length > 0 && (
        <ul className="mt-6 space-y-4">
          {item.learnings.map((l) => (
            <li key={l} className="flex gap-3.5">
              <span aria-hidden="true" className="mt-2.5 h-px w-3.5 flex-none bg-amber-deep/60" />
              <span className="prose-body">{l}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Experience = () => (
  <section id="experience" className="section bg-cream pt-0">
    <div className="shell">
      <SectionHeading
        number="02"
        label="Experience and education"
        title="Nine years making calls people acted on."
        intro="Operational systems, not prototypes. Fixed schedules, measured against what actually happened."
      />

      <div className="mt-16 grid gap-12 lg:grid-cols-12">
        <ol className="lg:col-span-8">
          {ROLES.map((role, i) => (
            <Role key={role.org} role={role} isLast={i === ROLES.length - 1} />
          ))}
        </ol>

        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-28">
            <figure className="m-0">
              <div className="overflow-hidden border border-ink/10">
                <img
                  src="/img/portrait-warm.webp"
                  alt="Ishita Srivastava"
                  loading="lazy"
                  className="aspect-[3/4] w-full object-cover"
                />
              </div>
            </figure>

            <dl className="mt-8 border-t border-ink/15 pt-6">
              {[
                ['Started at NCAR', '2016'],
                ['Left to found Aerveil', 'Oct 2025'],
                ['Agencies served', 'NOAA, NASA, USGS'],
              ].map(([k, v]) => (
                <div
                  key={k}
                  className="flex justify-between gap-4 border-b border-ink/10 py-3 last:border-0"
                >
                  <dt className="text-xs text-ink-soft">{k}</dt>
                  <dd className="metric text-xs text-ink">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </aside>
      </div>

      {/* Education, folded into this section rather than sitting on its own near
          the end of the page. */}
      <div className="mt-24 border-t border-ink/15 pt-14">
        <p className="eyebrow text-ink-soft">Education</p>
        <h3 className="display-lg mt-5 text-ink">Where the training came from.</h3>

        <div className="mt-12 grid gap-6 sm:grid-cols-2">
          {GRAD_PHOTOS.map((ph) => (
            <figure key={ph.src} className="m-0">
              <div className="overflow-hidden border border-ink/10">
                {/* Left unfaded on purpose: what is behind her is the content. */}
                <img
                  src={ph.src}
                  alt={ph.alt}
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover object-top"
                />
              </div>
              <figcaption className="mt-3 flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-mono text-[0.7rem] uppercase tracking-eyebrow text-ink">
                  {ph.caption}
                </span>
                <span className="font-mono text-[0.68rem] tracking-eyebrow text-ink-soft">
                  {ph.meta}
                </span>
              </figcaption>
            </figure>
          ))}
        </div>

        <div className="mt-16 space-y-12">
          {DEGREES.map((d) => (
            <Degree key={d.degree} item={d} />
          ))}
        </div>

        {/* Credentials */}
        <div className="mt-16 border-t border-ink/15 pt-10">
          <p className="eyebrow text-ink-soft">Credentials</p>
          <div className="mt-6 flex flex-col items-start gap-6 sm:flex-row sm:items-center">
            <a
              href={CREDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-none transition-transform duration-300 ease-calm hover:scale-[1.04]"
            >
              <img
                src="/img/claude-certified-architect.png"
                alt="Claude Certified Architect, Foundations badge. Verify on Credly."
                width="604"
                height="604"
                loading="lazy"
                className="h-28 w-28"
              />
            </a>
            <div>
              <p className="display-md text-ink">Claude Certified Architect</p>
              <p className="mt-2 font-mono text-[0.7rem] uppercase tracking-eyebrow text-ink-soft">
                Foundations · Anthropic
              </p>
              <p className="prose-body mt-3">
                Certified on designing and building production systems with Claude, which is the
                engine layer underneath the agentic work.
              </p>
              <a
                href={CREDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="link-underline mt-4 inline-flex items-center gap-1.5 font-mono text-[0.7rem] uppercase tracking-eyebrow text-ink-muted hover:text-amber-deep"
              >
                Verify on Credly
                <svg
                  className="h-2.5 w-2.5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  aria-hidden="true"
                >
                  <path d="M7 17L17 7M17 7H8M17 7v9" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default Experience;
